/**
 * Exclusive trees-hopper stages for the operator overview.
 * A property is in exactly one next hop — not "has AVM" overlapping counts.
 */
import { SKIP_ROW_ABSENT } from "./close-skip-lead.js";
import { isPersonOwner, isBusinessOwnerText } from "./owner-kind.js";
import { isAttomAvmRetryableFailure } from "./attom-avm.js";
import { OWNER_LOOKUP_SOURCE_NAMES, sourcesNeedOwnerLookup } from "./skip-jobs.js";
import {
  AUCTION_TIMING_BUCKETS,
  auctionProcessingWakeupDate,
  classifyAuctionTiming,
  resolveAuctionDate,
  type AuctionTimingBucket
} from "./auction-window.js";
import {
  auctionWindowPolicyFromWorkflow,
  getActivePipelineWorkflow,
  pastUnknownStatusWakeupDate,
  type RegisteredToolId
} from "./pipeline-workflow.js";
import {
  decidePropertiesConveyorHop,
  isOperatorHeldConveyorState,
  type PropertiesConveyorDecision,
  type PropertiesConveyorRow
} from "./properties-conveyor.js";
import { heirEstimateNeedsRun } from "./heir-estimate.js";
import {
  PIPELINE_TOOL_STALE_MS,
  hopFreshnessAt,
  hopHasExecutionEvidence,
  isPipelineServiceFailureClass,
  pipelineHopWorkClock,
  snapshotIsFresh,
  thrownHopEnvelope,
  type PipelineHopWorkClock
} from "./pipeline-tool-status.js";

export const TREES_PIPELINE_STAGE_IDS = [
  "auction_window",
  "need_owner",
  "need_skip",
  "need_value",
  "need_plv",
  "waiting",
  "need_equity",
  "need_close",
  "need_obit",
  "ready_to_call",
  "on_hold",
  "blocked",
  "closed"
] as const;

export type TreesPipelineStageId = (typeof TREES_PIPELINE_STAGE_IDS)[number];

/** Terminal exclusive hops. Not “waiting for processing.” */
export const TREES_PIPELINE_TERMINAL_STAGE_IDS = ["ready_to_call", "on_hold", "blocked", "closed"] as const;

export type TreesPipelineTerminalStageId = (typeof TREES_PIPELINE_TERMINAL_STAGE_IDS)[number];

export const TREES_PIPELINE_STAGE_LABELS: Record<TreesPipelineStageId, string> = {
  auction_window: "Auction window",
  need_owner: "Need owner",
  need_skip: "Need skip",
  need_value: "Need AVM / EPLV",
  need_plv: "Need loan value",
  waiting: "Waiting",
  need_equity: "Need equity",
  need_close: "Need Close",
  need_obit: "Waiting on obit",
  ready_to_call: "Ready to call",
  on_hold: "On hold",
  blocked: "Gated / blocked",
  closed: "Closed"
};

export type TreesPipelineSourceCount = {
  source: string;
  properties: number;
  lastSeenAt: string | null;
};

export type TreesPipelineAuctionTimingCounts = Record<AuctionTimingBucket, number>;

export type TreesPipelineDeadendCount = {
  class: string;
  count: number;
};

export type TreesPipelineStageWork = {
  eligible: number;
  executing: number | null;
  scheduledWait: number;
  providerWait: number;
  rejected: number;
  throughput: number | null;
  oldestEligibleAt: string | null;
  lastVerifiedSuccessAt: string | null;
  nextRetryAt: string | null;
  nextWakeupAt: string | null;
};

export type TreesPipelineLaneWork = TreesPipelineStageWork & {
  completed: number;
};

export type TreesPipelineStageCount = {
  id: TreesPipelineStageId;
  label: string;
  /** Exclusive hop total. ready + deadends. Houses are not counted again in the river. */
  count: number;
  /** Still actionable for this hop. The large number on /trees-pipeline. */
  ready: number;
  /** Tool pause on this hop. Ready houses stay ready; the header turns yellow. */
  sick: string | null;
  /** Last conveyor cycle actually worked this hop. Same 🟢 as ready, flashed on the board. */
  running: boolean;
  /** Held / cannot-go-forward / needs-a-fix piles. Breakdown of `count`, not a second river seat. */
  deadends: TreesPipelineDeadendCount[];
  auctionTiming: TreesPipelineAuctionTimingCounts;
  work?: TreesPipelineStageWork;
};

/** Lane-header and subtotal marks. Ready headers are green or yellow; piles are held / alert / stop. */
export const PIPELINE_PILE_MARK = {
  readyOk: "🟢",
  readySick: "🟡",
  held: "⏳",
  alert: "🚨",
  stop: "🛑"
} as const;

export type PipelinePileKind = "held" | "alert" | "stop";

const HELD_PILES = new Set([
  "over_30_days",
  "past_or_unknown",
  "invalid_sale_date",
  "auction_status_unknown",
  "ecourts_page_inaccessible",
  "ecourts_browser_lease_expired",
  "ecourts_missing_required_pdfs",
  "ecourts_collection_partial"
]);
const STOP_PILES = new Set([
  "blocked_no_owner",
  "sale_cancelled",
  "sale_complete",
  "invalid_owner_name",
  "obituary_no_match",
  "obituary_ambiguous_review",
  "close_not_deceased",
  "close_equity_not_finite",
  "close_equity_below_threshold",
  "close_obituary_required",
  "blocked_equity_below_25k",
  "skip_row_absent",
  "given_to_pi",
  "given_to_genealogist",
  "follow_up",
  "passed"
]);

/** Subtotal mark. Sick-tool pauses stay on the ready header as yellow, not a pile. */
export function pipelinePileKind(failureClass: string): PipelinePileKind | null {
  if (!failureClass) return null;
  if (HELD_PILES.has(failureClass)) return "held";
  if (STOP_PILES.has(failureClass)) return "stop";
  if (isPipelineServiceFailureClass(failureClass)) {
    // AVM-off after a Zillow miss is a house waiting on a tool we turned off.
    if (failureClass.startsWith("attom_avm_") || failureClass.endsWith("_pending")) return "alert";
    return null;
  }
  return "alert";
}

/** Unknown pile classes are alert so they cannot vanish from the header split. */
export function pipelinePileKindOrAlert(failureClass: string): PipelinePileKind {
  return pipelinePileKind(failureClass) ?? "alert";
}

export type TreesPipelineSummary = {
  properties: number;
  /** Exclusive hops that are still work. ready_to_call / on_hold / blocked are out. */
  waiting: number;
  latestScrapeAt: string | null;
  seenOnLatestScrape: number;
  sources: TreesPipelineSourceCount[];
  stages: TreesPipelineStageCount[];
  auctionTiming: TreesPipelineAuctionTimingCounts;
  blocker: TreesPipelineStageId | null;
  failures: Array<{ class: string; count: number }>;
  /** Optional heir-estimate work that is not the exclusive hop (Close already created). */
  heirEstimateEligible: number;
};

export type TreesPipelineInputRow = PropertiesConveyorRow & {
  sources?: unknown;
  sourceFields?: unknown;
  auctionDate?: unknown;
  conveyor_failure_class?: unknown;
  updatedAt?: unknown;
  updated_at?: unknown;
};

export type TreesPipelineSummaryOptions = {
  asOfDate?: unknown;
  /** Hop-level pause class keyed by exclusive stage. Not a house miss stamp. */
  hopPauses?: Partial<Record<TreesPipelineStageId, string>>;
  /** Last conveyor persist time. Older than CONVEYOR_DRAIN_STALE_MS yellows ready hops. */
  lastRunGeneratedAt?: unknown;
  /** Last-run hop actually worked this cycle. Board flashes the same 🟢. */
  hopRunning?: Partial<Record<TreesPipelineStageId, boolean>>;
  /** Last-run inflight/hit clocks. Missing hop stays null, not a fake zero. */
  hopWork?: Partial<Record<TreesPipelineStageId, PipelineHopWorkClock>>;
};

export const CONVEYOR_DRAIN_STALE_CLASS = "conveyor_drain_stale";
export const CONVEYOR_DRAIN_STALE_MS = PIPELINE_TOOL_STALE_MS;
export const TREES_PIPELINE_HOP_RUNNING_WINDOW_MS = PIPELINE_TOOL_STALE_MS;

export function isTreesPipelineStageId(value: string): value is TreesPipelineStageId {
  return (TREES_PIPELINE_STAGE_IDS as readonly string[]).includes(value);
}

export function isTreesPipelineTerminalStage(value: string): value is TreesPipelineTerminalStageId {
  return (TREES_PIPELINE_TERMINAL_STAGE_IDS as readonly string[]).includes(value);
}

/** ready + deadend piles. Falls back to `count` when the payload has no pile split. Hop identity, not lane headers. */
export function treesPipelineStagePileTotal(stage: {
  count: number;
  ready?: number;
  deadends?: readonly { count: number }[];
}): number {
  const dead = (stage.deadends ?? []).reduce((sum, row) => sum + row.count, 0);
  if (stage.ready == null && (stage.deadends == null || stage.deadends.length === 0)) return stage.count;
  return (stage.ready ?? 0) + dead;
}

/** Lane header: houses ready for the tool. Piles stay in detail subtotals. */
export function treesPipelineLaneHeaderCount(stage: { count: number; ready?: number }): number {
  return stage.ready ?? stage.count;
}

export type TreesPipelinePopulation = {
  properties: number;
  /** Ready to process (green or yellow headers), including ready-to-call. */
  ready: number;
  /** Held by design (auction more than 30 days out). */
  held: number;
  /** Needs a fix (tool/data problem). */
  alert: number;
  /** Cannot go forward (LLC, cancelled sale, no-match obit). Not Close.io. */
  stop: number;
  waiting: number;
  accounted: number;
  piled: number;
  terminals: number;
  gap: number;
};

/** Exclusive-hop identity: ready + held + alert + stop vs tree properties. Ingest is not houses. */
export function treesPipelinePopulation(summary: {
  properties: number;
  waiting?: number;
  stages: readonly {
    id: string;
    count: number;
    ready?: number;
    deadends?: readonly { class?: string; count: number }[];
  }[];
}): TreesPipelinePopulation {
  const accounted = summary.stages.reduce((sum, stage) => sum + stage.count, 0);
  let ready = 0;
  let held = 0;
  let alert = 0;
  let stop = 0;
  for (const stage of summary.stages) {
    ready += stage.ready ?? 0;
    for (const pile of stage.deadends ?? []) {
      const kind = pipelinePileKindOrAlert(String(pile.class ?? ""));
      if (kind === "held") held += pile.count;
      else if (kind === "stop") stop += pile.count;
      else alert += pile.count;
    }
  }
  const piled = ready + held + alert + stop;
  const terminals = summary.stages
    .filter((stage) => isTreesPipelineTerminalStage(stage.id))
    .reduce((sum, stage) => sum + stage.count, 0);
  return {
    properties: summary.properties,
    ready,
    held,
    alert,
    stop,
    waiting: summary.waiting ?? Math.max(0, accounted - terminals),
    accounted,
    piled,
    terminals,
    gap: summary.properties - piled
  };
}

/** Workflow step id → exclusive hopper stage. Ingest is tree freshness, not a house hop. */
export const TREES_PIPELINE_STEP_STAGE: Record<string, TreesPipelineStageId> = {
  auction_window: "auction_window",
  ecourts_upsert: "auction_window",
  owner: "need_owner",
  skip: "need_skip",
  value: "need_value",
  plv: "need_plv",
  waiting: "waiting",
  equity: "need_equity",
  obit: "need_obit",
  intestacy: "need_obit",
  close: "need_close",
  ready: "ready_to_call",
  hold: "on_hold",
  blocked: "blocked",
  closed: "closed"
};

/** Enrichment order matching decideWorkflowHop. Blocked is a reject sink, not Close completion. */
const LANE_COMPLETED_ORDER: TreesPipelineStageId[] = [
  "auction_window",
  "need_owner",
  "need_skip",
  "need_value",
  "need_plv",
  "waiting",
  "need_equity",
  "need_obit",
  "need_close",
  "ready_to_call",
  "on_hold",
  "closed"
];

export type PipelineLaneWorkKind = "scheduledWait" | "providerWait" | "rejected";

const PROVIDER_WAIT_PILES = new Set([
  "awaiting_skip_trace",
  "property_value_lookup_pending",
  "plv_lookup_pending",
  "ecourts_auction_date_pending",
  "auction_date_refresh_pending",
  "ecourts_upsert_pending"
]);

export function pipelineLaneWorkKind(failureClass: string): PipelineLaneWorkKind {
  if (!failureClass) return "rejected";
  if (HELD_PILES.has(failureClass)) return "scheduledWait";
  if (PROVIDER_WAIT_PILES.has(failureClass) || failureClass.endsWith("_pending")) return "providerWait";
  return "rejected";
}

export function treesPipelineSourceCompleteness(
  sources: readonly { source: string }[],
  expected: readonly string[]
): { present: number; expected: number; missing: string[] } {
  const presentIds = new Set(sources.map((row) => row.source));
  const missing = expected.filter((id) => !presentIds.has(id));
  return { present: expected.length - missing.length, expected: expected.length, missing };
}

function workFromPiles(
  ready: number,
  deadends: readonly { class: string; count: number }[],
  clocks: PipelineHopWorkClock | undefined,
  oldestEligibleAt: string | null,
  nextWakeupAt: string | null
): TreesPipelineStageWork {
  let scheduledWait = 0;
  let providerWait = 0;
  let rejected = 0;
  for (const pile of deadends) {
    const kind = pipelineLaneWorkKind(pile.class);
    if (kind === "scheduledWait") scheduledWait += pile.count;
    else if (kind === "providerWait") providerWait += pile.count;
    else rejected += pile.count;
  }
  return {
    eligible: ready,
    executing: clocks ? clocks.executing : null,
    scheduledWait,
    providerWait,
    rejected,
    throughput: clocks ? clocks.throughput : null,
    oldestEligibleAt,
    lastVerifiedSuccessAt: clocks?.lastVerifiedSuccessAt ?? null,
    nextRetryAt: clocks?.nextRetryAt ?? null,
    nextWakeupAt
  };
}

function laterClock(left: string | null, right: string | null): string | null {
  if (!left) return right;
  if (!right) return left;
  return left >= right ? left : right;
}

function earlierClock(left: string | null, right: string | null): string | null {
  if (!left) return right;
  if (!right) return left;
  return left <= right ? left : right;
}

/** Last-run inflight/hit clocks, keyed by exclusive hopper stage. Missing hop stays omitted. */
export function treesPipelineHopWorkFromLastRun(
  lastRun: unknown
): Partial<Record<TreesPipelineStageId, PipelineHopWorkClock>> {
  if (!lastRun || typeof lastRun !== "object" || Array.isArray(lastRun)) return {};
  const rec = lastRun as Record<string, unknown>;
  const clocks: Partial<Record<TreesPipelineStageId, PipelineHopWorkClock>> = {};
  for (const [key, stage] of LAST_RUN_HOP_STAGES) {
    const clock = pipelineHopWorkClock(rec[key]);
    if (!clock) continue;
    const prior = clocks[stage];
    clocks[stage] = prior
      ? {
          executing: prior.executing + clock.executing,
          throughput: prior.throughput + clock.throughput,
          lastVerifiedSuccessAt: laterClock(prior.lastVerifiedSuccessAt, clock.lastVerifiedSuccessAt),
          nextRetryAt: earlierClock(prior.nextRetryAt, clock.nextRetryAt)
        }
      : clock;
  }
  return clocks;
}

function stageCountById(
  summary: { stages: readonly { id: string; count: number }[] }
): Map<string, number> {
  return new Map(summary.stages.map((stage) => [stage.id, stage.count]));
}

/** Houses that have left this hop for a later exclusive hop. Blocked is a reject sink, not Close completion. */
export function treesPipelineCompletedCount(
  stepId: string,
  summary: { stages: readonly { id: string; count: number }[] }
): number {
  const stageId = TREES_PIPELINE_STEP_STAGE[stepId];
  if (!stageId) return 0;
  const index = LANE_COMPLETED_ORDER.indexOf(stageId);
  if (index < 0) return 0;
  const counts = stageCountById(summary);
  let completed = 0;
  for (const later of LANE_COMPLETED_ORDER.slice(index + 1)) {
    completed += counts.get(later) ?? 0;
  }
  return completed;
}

export function treesPipelineLaneWork(
  stepId: string,
  summary: {
    properties: number;
    stages: readonly {
      id: string;
      count: number;
      ready?: number;
      deadends?: readonly { class?: string; count: number }[];
      work?: TreesPipelineStageWork;
    }[];
  }
): TreesPipelineLaneWork | null {
  if (stepId === "ingest") return null;
  const stageId = TREES_PIPELINE_STEP_STAGE[stepId];
  if (!stageId) return null;
  const stage = summary.stages.find((row) => row.id === stageId);
  if (!stage) return null;
  const completed = treesPipelineCompletedCount(stepId, summary);
  const deadends = (stage.deadends ?? []).map((row) => ({ class: String(row.class ?? ""), count: row.count }));
  if (stepId === "intestacy") {
    const overlay = "heirEstimateEligible" in summary
      ? Number((summary as { heirEstimateEligible?: number }).heirEstimateEligible ?? 0)
      : 0;
    const intestacyPiles = deadends.filter((row) => row.class.includes("intestacy"));
    return {
      ...workFromPiles(overlay, intestacyPiles, undefined, null, null),
      completed
    };
  }
  if (stage.work) return { ...stage.work, completed };
  return {
    ...workFromPiles(stage.ready ?? 0, deadends, undefined, null, null),
    completed
  };
}

/** Exclusive hop + optional deadend class. Same math as /trees-pipeline counts. */
export function classifyTreesPipelinePile(
  row: TreesPipelineInputRow,
  options: TreesPipelineSummaryOptions = {}
): { stage: TreesPipelineStageId; deadend: string | null } {
  const auctionDate = row.auctionDate ?? resolveAuctionDate(row.sourceFields, options.asOfDate);
  const decision = decidePropertiesConveyorHop({ ...row, auctionDate }, options.asOfDate);
  const stage = treesPipelineStageFromDecision(decision);
  return {
    stage,
    deadend: treesPipelineDeadendClass(stage, row, options, decision)
      ?? fallbackToolAlert(stage, options.hopPauses, decision)
  };
}

const LAST_RUN_HOP_STAGES: ReadonlyArray<readonly [string, TreesPipelineStageId]> = [
  ["auctionDateRefresh", "auction_window"],
  ["ecourtsUpsert", "auction_window"],
  ["reportall", "need_owner"],
  ["attomOwner", "need_owner"],
  ["smartskip", "need_skip"],
  ["skipFromClose", "need_skip"],
  ["zillow", "need_value"],
  ["avm", "need_value"],
  ["plv", "need_plv"],
  ["equity", "need_equity"],
  ["closeCreate", "need_close"],
  ["closeStatus", "need_close"],
  ["obituary", "need_obit"],
  ["intestacy", "need_obit"]
];

/** Last conveyor cycle pauses, keyed by exclusive hopper stage. */
export function treesPipelineHopPausesFromLastRun(
  lastRun: unknown
): Partial<Record<TreesPipelineStageId, string>> {
  if (!lastRun || typeof lastRun !== "object" || Array.isArray(lastRun)) return {};
  const rec = lastRun as Record<string, { paused?: unknown } | undefined>;
  const pauses: Partial<Record<TreesPipelineStageId, string>> = {};
  for (const [hop, stage] of LAST_RUN_HOP_STAGES) {
    const paused = hopPauseClass(rec[hop]);
    if (paused && !pauses[stage]) pauses[stage] = paused;
  }
  return pauses;
}

/** Last conveyor cycle work, keyed by exclusive hopper stage. Stale last-run is idle. */
export function treesPipelineHopRunningFromLastRun(
  lastRun: unknown,
  now: number = Date.now()
): Partial<Record<TreesPipelineStageId, boolean>> {
  if (!lastRun || typeof lastRun !== "object" || Array.isArray(lastRun)) return {};
  const rec = lastRun as Record<string, unknown>;
  const generatedAt = typeof rec.generatedAt === "string" ? rec.generatedAt : null;
  const clock = Number.isFinite(now) ? now : Date.now();
  const running: Partial<Record<TreesPipelineStageId, boolean>> = {};
  for (const [key, stage] of LAST_RUN_HOP_STAGES) {
    if (!hopDidWork(rec[key])) continue;
    if (snapshotIsFresh(hopFreshnessAt(rec[key], generatedAt), clock)) running[stage] = true;
  }
  return running;
}

function hopPauseClass(hop: unknown): string | null {
  if (!hop || typeof hop !== "object" || Array.isArray(hop)) return null;
  const rec = hop as Record<string, unknown>;
  if (typeof rec.paused === "string" && rec.paused.trim() && isPipelineServiceFailureClass(rec.paused)) {
    return rec.paused.trim();
  }
  return thrownHopEnvelope(rec)?.failureClass ?? null;
}

function hopDidWork(hop: unknown): boolean {
  return hopHasExecutionEvidence(hop);
}

/** Yellow header: the hop that would drain ready houses is sick. AVM-off is an alert pile, not a yellow value header. */
function laneSickMark(
  stage: TreesPipelineStageId,
  hopPauses: TreesPipelineSummaryOptions["hopPauses"]
): string | null {
  const paused = hopPauses?.[stage];
  if (!paused) return null;
  if (stage === "need_value" && paused.startsWith("attom_")) return null;
  return paused;
}

/** Houses whose next tool is a paused fallback (ATTOM AVM off after Zillow miss). */
function fallbackToolAlert(
  stage: TreesPipelineStageId,
  hopPauses: TreesPipelineSummaryOptions["hopPauses"],
  decision?: PropertiesConveyorDecision
): string | null {
  const paused = hopPauses?.[stage];
  if (!paused || !isPipelineServiceFailureClass(paused)) return null;
  if (stage === "need_value" && paused.startsWith("attom_") && decision?.action === "avm") return paused;
  return null;
}

export function treesPipelineStageFromDecision(decision: PropertiesConveyorDecision): TreesPipelineStageId {
  switch (decision.action) {
    case "reportall":
    case "attom_owner":
      return "need_owner";
    case "smartskip":
      return "need_skip";
    case "avm":
    case "zillow":
      return "need_value";
    case "plv":
      return "need_plv";
    case "equity":
      return "need_equity";
    case "close_create":
      return "need_close";
    case "obituary":
    case "intestacy":
      return "need_obit";
    case "auction_date_refresh":
    case "ecourts_upsert":
      return "auction_window";
    case "status_ready":
    case "status_hold":
      return "need_close";
    case "none":
      if (decision.stepId === "waiting" || decision.failureClass === "over_30_days") return "waiting";
      if (decision.stepId === "closed" || decision.failureClass === "sale_cancelled" || decision.failureClass === "sale_complete" || decision.failureClass === "invalid_owner_name") return "closed";
      if (decision.stepId === "auction_window" || decision.stepId === "ecourts_upsert") return "auction_window";
      if (decision.stepId === "plv") return "need_plv";
      if (decision.state === "ready_to_call") return "ready_to_call";
      if (decision.state === "on_hold") return "on_hold";
      if (isOperatorHeldConveyorState(decision.state)) return "on_hold";
      return "blocked";
    default:
      return "blocked";
  }
}

export function summarizeTreesPipeline(
  rows: readonly TreesPipelineInputRow[],
  options: TreesPipelineSummaryOptions = {}
): TreesPipelineSummary {
  const treeRows = rows.filter((row) => sourcesNeedOwnerLookup(row.sources));
  const stageCounts = Object.fromEntries(TREES_PIPELINE_STAGE_IDS.map((id) => [id, 0])) as Record<TreesPipelineStageId, number>;
  const stageReady = Object.fromEntries(TREES_PIPELINE_STAGE_IDS.map((id) => [id, 0])) as Record<TreesPipelineStageId, number>;
  const stageDeadends = Object.fromEntries(TREES_PIPELINE_STAGE_IDS.map((id) => [id, new Map<string, number>()])) as Record<
    TreesPipelineStageId,
    Map<string, number>
  >;
  const stageAuctionTiming = Object.fromEntries(
    TREES_PIPELINE_STAGE_IDS.map((id) => [id, emptyAuctionTiming()])
  ) as Record<TreesPipelineStageId, TreesPipelineAuctionTimingCounts>;
  const auctionTiming = emptyAuctionTiming();
  const stageOldestEligible = Object.fromEntries(TREES_PIPELINE_STAGE_IDS.map((id) => [id, null as string | null])) as Record<TreesPipelineStageId, string | null>;
  const workflow = getActivePipelineWorkflow();
  let heirEstimateEligible = 0;
  const stageNextWakeup = Object.fromEntries(TREES_PIPELINE_STAGE_IDS.map((id) => [id, null as string | null])) as Record<TreesPipelineStageId, string | null>;
  const windowDays = auctionWindowPolicyFromWorkflow(workflow).auctionWindowDays;
  const failures = new Map<string, number>();
  const sources = OWNER_LOOKUP_SOURCE_NAMES.map((source) => ({
    source,
    properties: 0,
    lastSeenAt: null as string | null
  }));
  let latestScrapeAt: string | null = null;

  for (const row of treeRows) {
    const { stage, deadend } = classifyTreesPipelinePile(row, options);
    if (heirEstimateNeedsRun(row, workflow)) heirEstimateEligible += 1;
    stageCounts[stage] += 1;
    if (deadend && pipelinePileKind(deadend)) {
      stageDeadends[stage].set(deadend, (stageDeadends[stage].get(deadend) ?? 0) + 1);
      if (deadend === "over_30_days") {
        const wakeup = auctionProcessingWakeupDate({
          auctionDate: row.auctionDate ?? resolveAuctionDate(row.sourceFields, options.asOfDate),
          ...(windowDays != null ? { windowDays } : {})
        });
        if (wakeup) stageNextWakeup[stage] = earlierClock(stageNextWakeup[stage], wakeup);
      } else if (deadend === "past_or_unknown" || deadend === "auction_status_unknown") {
        const wakeup = pastUnknownStatusWakeupDate(row);
        if (wakeup) stageNextWakeup[stage] = earlierClock(stageNextWakeup[stage], wakeup);
      }
    } else {
      stageReady[stage] += 1;
      const updated = text(row.updatedAt ?? row.updated_at);
      if (updated) stageOldestEligible[stage] = earlierClock(stageOldestEligible[stage], updated);
    }
    const timing = classifyAuctionTiming({
      auctionDate: row.auctionDate ?? resolveAuctionDate(row.sourceFields, options.asOfDate),
      asOfDate: options.asOfDate,
      auctionWindowDays: auctionWindowPolicyFromWorkflow().auctionWindowDays
    });
    stageAuctionTiming[stage][timing] += 1;
    auctionTiming[timing] += 1;
    const failure = text(row.conveyorFailureClass ?? row.conveyor_failure_class);
    if (failure) failures.set(failure, (failures.get(failure) ?? 0) + 1);
    const parsed = parseSources(row.sources);
    for (const entry of sources) {
      const seen = sourceLastSeen(parsed[entry.source]);
      if (!seen && parsed[entry.source] == null) continue;
      if (parsed[entry.source] == null) continue;
      entry.properties += 1;
      if (seen && (!entry.lastSeenAt || seen > entry.lastSeenAt)) entry.lastSeenAt = seen;
      if (seen && (!latestScrapeAt || seen > latestScrapeAt)) latestScrapeAt = seen;
    }
  }

  const latestDay = latestScrapeAt ? latestScrapeAt.slice(0, 10) : null;
  let seenOnLatestScrape = 0;
  if (latestDay) {
    for (const row of treeRows) {
      const parsed = parseSources(row.sources);
      const maxSeen = OWNER_LOOKUP_SOURCE_NAMES.reduce((max, name) => {
        const seen = sourceLastSeen(parsed[name]);
        return seen && seen > max ? seen : max;
      }, "");
      if (maxSeen.slice(0, 10) === latestDay) seenOnLatestScrape += 1;
    }
  }

  const stages = TREES_PIPELINE_STAGE_IDS.map((id) => {
    const deadends = [...stageDeadends[id].entries()]
      .map(([className, count]) => ({ class: className, count }))
      .sort((left, right) => right.count - left.count || left.class.localeCompare(right.class));
    return {
      id,
      label: TREES_PIPELINE_STAGE_LABELS[id],
      count: stageCounts[id],
      ready: stageReady[id],
      sick: laneSickMark(id, options.hopPauses),
      running: Boolean(options.hopRunning?.[id]),
      deadends,
      auctionTiming: stageAuctionTiming[id],
      work: workFromPiles(
        stageReady[id],
        deadends,
        options.hopWork?.[id],
        stageOldestEligible[id],
        stageNextWakeup[id]
      )
    };
  });
  const workOrder: TreesPipelineStageId[] = [
    "need_owner",
    "need_skip",
    "need_value",
    "need_plv",
    "need_equity",
    "need_close",
    "need_obit"
  ];
  const blocker = workOrder.find((id) => stageReady[id] > 0) ?? null;
  const terminals = TREES_PIPELINE_TERMINAL_STAGE_IDS.reduce((sum, id) => sum + stageCounts[id], 0);

  return {
    properties: treeRows.length,
    waiting: treeRows.length - terminals,
    latestScrapeAt,
    seenOnLatestScrape,
    sources: sources.filter((entry) => entry.properties > 0),
    stages,
    auctionTiming,
    blocker,
    heirEstimateEligible,
    failures: [...failures.entries()]
      .map(([className, count]) => ({ class: className, count }))
      .sort((left, right) => right.count - left.count)
  };
}

function emptyAuctionTiming(): TreesPipelineAuctionTimingCounts {
  return Object.fromEntries(AUCTION_TIMING_BUCKETS.map((bucket) => [bucket, 0])) as TreesPipelineAuctionTimingCounts;
}

function parseSources(value: unknown): Record<string, unknown> {
  if (value == null || value === "") return {};
  if (typeof value === "string") {
    try {
      return parseSources(JSON.parse(value));
    } catch {
      return {};
    }
  }
  if (typeof value !== "object" || Array.isArray(value)) return {};
  return value as Record<string, unknown>;
}

function sourceLastSeen(value: unknown): string | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const rec = value as Record<string, unknown>;
  const seen = rec.lastSeenAt ?? rec.last_seen_at;
  return typeof seen === "string" && seen.trim() ? seen.trim() : null;
}

function text(value: unknown): string {
  return value == null ? "" : String(value).replaceAll(/\s+/g, " ").trim();
}

function treesPipelineDeadendClass(
  stage: TreesPipelineStageId,
  row: TreesPipelineInputRow,
  options: TreesPipelineSummaryOptions = {},
  decision?: PropertiesConveyorDecision
): string | null {
  if (stage === "waiting") return "over_30_days";
  if (stage === "closed") {
    return text(decision?.failureClass) || failureStamp(row) || "sale_complete";
  }
  if (stage === "auction_window") {
    const failure = String(decision?.failureClass ?? "");
    if (failure === "sale_cancelled" || failure === "sale_complete" || failure === "invalid_owner_name" || failure === "over_30_days") {
      return null;
    }
    if (failure === "invalid_sale_date" || failure === "auction_status_unknown") return failure;
    if (
      failure === "ecourts_auction_date_miss"
      || failure === "ecourts_auction_no_result"
      || failure === "ecourts_page_inaccessible"
      || failure === "ecourts_browser_lease_expired"
      || failure === "ecourts_missing_required_pdfs"
      || failure === "ecourts_collection_partial"
    ) return failure;
    const timing = classifyAuctionTiming({
      auctionDate: row.auctionDate ?? resolveAuctionDate(row.sourceFields, options.asOfDate),
      asOfDate: options.asOfDate,
      auctionWindowDays: auctionWindowPolicyFromWorkflow().auctionWindowDays
    });
    if (timing === "over_30_days") return null;
    if (failure === "past_or_unknown" && decision?.action !== "auction_date_refresh") {
      return "past_or_unknown";
    }
    if (timing === "past_or_unknown" && decision?.action !== "auction_date_refresh") return timing;
    const stamp = failureStamp(row);
    return closedHopMiss(toolsForStage("auction_window"), collectToolMisses(row))
      ?? (stamp === "ecourts_missing_required_pdfs" ? stamp : null);
  }
  if (stage === "need_owner") {
    const misses = collectToolMisses(row);
    if (unusableOwner(row)) return "blocked_no_owner";
    return closedHopMiss(toolsForStage("need_owner"), misses);
  }
  if (stage === "need_skip") {
    const stamp = failureStamp(row);
    if (stamp === "awaiting_skip_trace") return stamp;
    return closedHopMiss(toolsForStage("need_skip"), collectToolMisses(row));
  }
  if (stage === "need_value") {
    const stamp = failureStamp(row);
    return closedHopMiss(stepTools("value"), collectToolMisses(row))
      ?? (stamp === "property_value_lookup_pending" ? stamp : null);
  }
  if (stage === "need_plv") {
    const failure = String(decision?.failureClass ?? "");
    if (failure === "blocked_no_plv") return failure;
    const stamp = failureStamp(row);
    return closedHopMiss(stepTools("plv"), collectToolMisses(row))
      ?? (stamp === "blocked_no_plv" ? stamp : null);
  }
  if (stage === "need_equity") {
    const stamp = failureStamp(row);
    return stamp === "blocked_no_equity" ? stamp : null;
  }
  if (stage === "need_close") {
    return closedHopMiss(toolsForStage("need_close"), collectToolMisses(row))
      ?? closeStamp(failureStamp(row))
      ?? closeStamp(decision?.failureClass ?? "");
  }
  if (stage === "need_obit") {
    return closedHopMiss(stepTools("obit"), collectToolMisses(row))
      ?? closedHopMiss(stepTools("intestacy"), collectToolMisses(row));
  }
  if (isOperatorHeldConveyorState(decision?.state ?? row.conveyorState)) {
    return String(decision?.state ?? row.conveyorState);
  }
  if (stage === "on_hold") {
    return text(decision?.failureClass) || failureStamp(row) || "on_hold";
  }
  if (stage === "blocked") {
    return text(decision?.failureClass) || failureStamp(row) || "blocked_unclassified";
  }
  return null;
}

function closeStamp(stamp: string): string | null {
  return stamp.startsWith("close_") || stamp === "blocked_no_equity" ? stamp : null;
}

function unusableOwner(row: TreesPipelineInputRow): boolean {
  const ownerRaw = row.ownerRaw;
  const firstName = row.firstName;
  const lastName = row.lastName;
  if (isPersonOwner({ firstName, lastName, ownerRaw })) return false;
  if (isBusinessOwnerText(ownerRaw) || isBusinessOwnerText(firstName) || isBusinessOwnerText(lastName)) return true;
  return Boolean(text(ownerRaw) || text(firstName) || text(lastName));
}

function closedHopMiss(tools: readonly RegisteredToolId[], misses: Record<string, string>): string | null {
  if (tools.length === 0) return null;
  let last: string | null = null;
  for (const toolId of tools) {
    const miss = misses[toolId];
    if (!miss || isPipelineServiceFailureClass(miss)) return null;
    last = miss;
  }
  return last;
}

function collectToolMisses(row: TreesPipelineInputRow): Record<string, string> {
  const fields = parseSources(row.sourceFields);
  const misses: Record<string, string> = {
    ...asMissMap(fields.toolMisses),
    ...asMissMap(row.toolMisses)
  };
  const stamp = failureStamp(row);
  if (stamp === "blocked_no_value") {
    misses.attom_avm ??= stamp;
    misses.zillow_zestimate ??= stamp;
    return misses;
  }
  const toolId = toolForStamp(stamp);
  if (toolId && !misses[toolId] && isTypedMiss(toolId, stamp)) misses[toolId] = stamp;
  return misses;
}

function asMissMap(value: unknown): Record<string, string> {
  const parsed = typeof value === "string" ? parseSources(value) : value;
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
  return Object.fromEntries(
    Object.entries(parsed as Record<string, unknown>)
      .filter(([, miss]) => typeof miss === "string" && String(miss).trim())
      .map(([key, miss]) => [key, String(miss)])
  );
}

function failureStamp(row: TreesPipelineInputRow): string {
  return text(row.conveyorFailureClass ?? row.conveyor_failure_class);
}

function toolForStamp(stamp: string): RegisteredToolId | null {
  if (stamp.startsWith("reportall_")) return "reportall_owner";
  if (stamp.startsWith("attom_owner_")) return "attom_owner";
  if (stamp.startsWith("attom_avm_") || stamp === "blocked_no_value") return "attom_avm";
  if (stamp.startsWith("property_value_") || stamp.startsWith("property_found_") || stamp === "property_address_invalid") {
    return "zillow_zestimate";
  }
  if (stamp === SKIP_ROW_ABSENT || stamp === "awaiting_skip_trace" || stamp.startsWith("smartskip_")) return "smartskip";
  if (stamp === "blocked_no_plv" || (stamp.startsWith("ecourts_sp_plv") && stamp.endsWith("_miss"))) return "ecourts_sp_plv";
  if (stamp.startsWith("obituary_")) return "obituary_google";
  return null;
}

function isTypedMiss(toolId: RegisteredToolId, stamp: string): boolean {
  if (!stamp) return false;
  switch (toolId) {
    case "reportall_owner":
      return stamp === "reportall_no_match" || stamp === "reportall_input_invalid" || stamp === "reportall_ambiguous_match";
    case "attom_owner":
      return stamp === "attom_owner_no_match" || stamp === "attom_owner_address_mismatch" || stamp === "attom_owner_input_invalid" || stamp === "attom_owner_ambiguous_match";
    case "smartskip":
      return stamp === SKIP_ROW_ABSENT || stamp === "awaiting_skip_trace" || stamp.startsWith("smartskip_");
    case "zillow_zestimate":
      return stamp === "property_value_no_match"
        || stamp === "property_value_address_mismatch"
        || stamp === "property_value_ambiguous_match"
        || stamp === "property_found_value_unavailable"
        || stamp === "property_value_unavailable"
        || stamp === "property_found_active_listing"
        || stamp === "property_address_invalid";
    case "attom_avm":
      return stamp === "blocked_no_value"
        || (stamp.startsWith("attom_avm_") && !isAttomAvmRetryableFailure(stamp) && stamp !== "attom_avm_not_configured" && stamp !== "attom_avm_auth_failed");
    case "ecourts_sp_plv":
      return stamp.startsWith("ecourts_sp_plv") && stamp.endsWith("_miss");
    default:
      return false;
  }
}

function toolsForStage(stage: TreesPipelineStageId): RegisteredToolId[] {
  if (stage === "need_owner") return stepTools("owner");
  if (stage === "need_skip") return stepTools("skip");
  if (stage === "need_value") return stepTools("value");
  if (stage === "need_plv") return stepTools("plv");
  if (stage === "need_equity") return stepTools("equity");
  if (stage === "need_close") return stepTools("close");
  if (stage === "need_obit") return [...stepTools("obit"), ...stepTools("intestacy")];
  if (stage === "auction_window") return stepTools("auction_window");
  return [];
}

function stepTools(stepId: string): RegisteredToolId[] {
  return getActivePipelineWorkflow().steps.find((step) => step.id === stepId)?.tools ?? [];
}


